import { api } from "./client";
import type {
  ActivitiesResponse,
  ActivityDetail,
  DeviceWellnessEntry,
  GearItem,
  MemoryYearGroup,
  PhotosResponse,
  PlannedWorkout,
  Tag,
  TrainingPlanDetail,
  TrainingPlanSummary,
  User,
  WellnessEntry,
} from "./types";

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  return search.size > 0 ? `?${search.toString()}` : "";
}

export const getMe = () => api.get<User>("/api/v1/users/me");

export const updateProfile = (body: {
  displayName?: string | null;
  timezone?: string;
  weightUnit?: "kg" | "lb";
}) => api.put<User>("/api/v1/users/me", body);

export const updatePreferences = (body: {
  easyPaceBaselineSecPerKm?: number | null;
  maxHeartRateBpm?: number | null;
  restingHeartRateBpm?: number | null;
  lactateThresholdHr?: number | null;
  ageBorn?: number | null;
  enableRunningFeatures?: boolean;
}) => api.put<User>("/api/v1/users/me/preferences", body);

export const listActivities = (params: {
  page?: number;
  from?: string;
  to?: string;
  activityType?: string;
} = {}) => {
  const limit = 20;
  const offset = ((params.page ?? 1) - 1) * limit;
  return api.get<ActivitiesResponse>(
    `/api/v1/activities${query({ limit, offset, from: params.from, to: params.to, activityType: params.activityType })}`,
  );
};

export const getActivity = (id: string) => api.get<ActivityDetail>(`/api/v1/activities/${id}`);
export const updateActivityNotes = (id: string, notes: string | null) =>
  api.put(`/api/v1/activities/${id}/notes`, { notes });
export const updateActivityEffort = (id: string, perceivedEffort: number | null) =>
  api.put(`/api/v1/activities/${id}/effort`, { perceivedEffort });
export const assignActivityGear = (id: string, gearId: string) =>
  api.post(`/api/v1/activities/${id}/gear`, { gearId });
export const removeActivityGear = (id: string, gearId: string) =>
  api.delete(`/api/v1/activities/${id}/gear/${gearId}`);
export const setActivityTags = (id: string, tags: string[]) =>
  api.post<{ tags: string[] }>(`/api/v1/activities/${id}/tags`, { tags });
export const clearActivityTags = (id: string) => api.delete(`/api/v1/activities/${id}/tags`);

export const getWellnessToday = () =>
  api.get<{ entry: WellnessEntry | null; readinessScore: number | null }>("/api/v1/wellness/today");
export const saveWellnessToday = (body: {
  sleepQuality?: number | null;
  muscleSoreness?: number | null;
  energyLevel?: number | null;
  mood?: number | null;
  notes?: string | null;
}) => api.put<{ entry: WellnessEntry; readinessScore: number | null }>("/api/v1/wellness/today", body);
export const getWellnessHistory = (from?: string, to?: string) =>
  api.get<{ entries: WellnessEntry[] }>(`/api/v1/wellness/history${query({ from, to })}`);
export const getDeviceWellness = (from?: string, to?: string) =>
  api.get<{ entries: DeviceWellnessEntry[] }>(`/api/v1/wellness/device${query({ from, to })}`);
export const getLatestDeviceWellness = () =>
  api.get<{ entry: DeviceWellnessEntry | null }>("/api/v1/wellness/device/latest");

export const listPhotos = (params: { page?: number; type?: string; year?: number | string } = {}) =>
  api.get<PhotosResponse>(`/api/v1/photos${query(params)}`);
export const getPhotoMemories = () => api.get<{ years: MemoryYearGroup[]; today: string }>("/api/v1/photos/memories");

export const listTrainingPlans = () => api.get<{ plans: TrainingPlanSummary[] }>("/api/v1/training/plans");
export const getTrainingPlan = (id: string) => api.get<TrainingPlanDetail>(`/api/v1/training/plans/${id}`);
export const getTrainingCalendar = (from?: string, to?: string) =>
  api.get<{ workouts: PlannedWorkout[]; from: string; to: string }>(`/api/v1/training/calendar${query({ from, to })}`);
export const completeWorkout = (id: string) => api.post<{ workout: PlannedWorkout }>(`/api/v1/training/workouts/${id}/complete`);
export const skipWorkout = (id: string) => api.post<{ workout: PlannedWorkout }>(`/api/v1/training/workouts/${id}/skip`);
export const associateWorkout = (id: string, activityId: string) =>
  api.post<{ workout: PlannedWorkout }>(`/api/v1/training/workouts/${id}/associate`, { activityId });

export const listGear = (params: { includeRetired?: boolean; type?: string } = {}) =>
  api.get<{ gear: GearItem[] }>(`/api/v1/gear${query(params)}`);
export const createGear = (body: {
  gearType: string;
  name: string;
  brand?: string | null;
  model?: string | null;
  purchaseDate?: string | null;
  notes?: string | null;
}) => api.post<GearItem>("/api/v1/gear", body);
export const updateGear = (id: string, body: {
  name?: string;
  brand?: string | null;
  model?: string | null;
  purchaseDate?: string | null;
  notes?: string | null;
}) => api.put<GearItem>(`/api/v1/gear/${id}`, body);
export const retireGear = (id: string, retiredDate?: string) =>
  api.post(`/api/v1/gear/${id}/retire`, retiredDate ? { retiredDate } : {});

export const listTags = () => api.get<{ tags: Tag[] }>("/api/v1/tags");
